// Stage 4, debrief (spec §2 Stage 4). One scrollable page: the outcome, the split replay on a shared
// scrubber, the order log, the decision log with red's reasoning revealed, the synchronization matrix
// the run wrote for itself, the losses/consumption/picture-accuracy figures, the computed lost-time
// list, and last the rapporteur's narrative — which is built only from everything above it.

import { useEffect, useMemo } from 'react';
import { copAccuracy, decisionLog, decisionPoints, decisionPointsSummary, lostTime } from '../../debrief/analyze';
import type { DebriefContext } from '../../ai/templates';
import type { Event, Snapshot } from '../../sim/types';
import { TICKS_PER_MIN } from '../../sim/types';
import { useDebrief } from '../../state/debrief';
import { useStore } from '../../state/store';
import { useBranch } from '../debrief/branchState';
import { Calibration } from '../debrief/Calibration';
import { DecisionSupport } from '../debrief/DecisionSupport';
import { LostTime } from '../debrief/LostTime';
import { DecisionLog, OrderLogTable } from '../debrief/Logs';
import { Narrative } from '../debrief/Narrative';
import { Replay } from '../debrief/Replay';
import { lossesLine, Stats } from '../debrief/Stats';
import { SyncMatrix } from '../debrief/SyncMatrix';
import { brokenFromEvents, lossesFromTruth, statsRecord, type RunStats } from '../debrief/format';
import { clockHM } from '../reports';
import { Wordmark } from '../Wordmark';
import { ShareChip } from '../ShareChip';
import { DEBUG } from '../../debug';

const OUTCOME: Record<NonNullable<Snapshot['ended']> | 'unknown', { label: string; tone: string; note: string }> = {
  seized: {
    label: 'OBJECTIVE SEIZED',
    // Friendly cyan, amber, fog: the outcome is stated in the same three hues the map uses for
    // "ours", "a warning" and "nothing conclusive" — not a green tick and a red cross.
    tone: 'var(--color-friendly)',
    note: 'Three maneuver platoons held the objective with no living enemy inside it for five minutes.',
  },
  culminated: {
    label: 'CULMINATED',
    tone: 'var(--color-warn)',
    note: 'The task force ran out of the combat power or the fuel to continue the attack.',
  },
  time: {
    label: 'TIME EXPIRED',
    tone: 'var(--color-fog)',
    note: 'The clock ran out with the objective still in enemy hands.',
  },
  unknown: {
    label: 'RUN ENDED',
    tone: 'var(--color-fog)',
    note: 'The run ended without a recorded outcome.',
  },
};

export function DebriefStage() {
  const payload = useDebrief((s) => s.payload);
  const scenario = useStore((s) => s.scenario);
  const meta = useStore((s) => s.meta);
  const grid = useStore((s) => s.grid);
  const dials = useStore((s) => s.dials);
  const plan = useStore((s) => s.plan);
  const setStage = useStore((s) => s.setStage);

  const graphics = useMemo(() => scenario?.graphics ?? [], [scenario]);
  const snap = payload?.snap ?? null;
  const events = useMemo<Event[]>(() => payload?.events ?? [], [payload]);
  const units = useMemo(() => snap?.units ?? [], [snap]);
  const orders = useMemo(() => snap?.orders ?? [], [snap]);
  const truth = useMemo(() => payload?.truth ?? [], [payload]);

  const decisions = useMemo(() => decisionLog(events, orders), [events, orders]);
  // Pitch 6: the decision points are computed once here and used three times — the Decision Support
  // Template draws them, the matrix lists them, and the rapporteur is told about them.
  const dps = useMemo(
    () => decisionPoints(events, orders, graphics, units, scenario),
    [events, orders, graphics, units, scenario],
  );
  const dpSummary = useMemo(() => decisionPointsSummary(dps), [dps]);
  const runTicks = snap?.tick ?? 0;
  const lostTimeFull = useMemo(
    () => lostTime(events, orders, graphics, units, runTicks),
    [events, orders, graphics, units, runTicks],
  );

  const stats = useMemo<RunStats>(() => {
    const contactEvents = events.filter((e) => e.kind === 'contact.new' && e.side === 0);
    const cop = grid
      ? copAccuracy(truth, contactEvents, units, grid)
      : { contactsCorrectType: 0, meanPositionErrorM: null, typeRate: null, enemiesNeverSeen: 0 };
    const losses = lossesFromTruth(truth, units);
    const broken = brokenFromEvents(events, units);
    const quoted = contactEvents.reduce(
      (sum, e) => sum + (typeof e.payload.errorM === 'number' ? e.payload.errorM : 0),
      0,
    );
    return {
      blueLosses: losses.blue,
      blueBroken: broken.blue,
      blueTotal: losses.blueTotal,
      redLosses: losses.red,
      redBroken: broken.red,
      redTotal: losses.redTotal,
      fuelPct: snap?.force.fuel ?? 0,
      ammoPct: snap?.force.ammo ?? 0,
      strengthPct: snap?.force.strength ?? 0,
      contacts: contactEvents.length,
      contactsCorrectType: cop.contactsCorrectType,
      meanPositionErrorM: cop.meanPositionErrorM,
      typeRate: cop.typeRate,
      meanReportedErrorM: contactEvents.length > 0 ? quoted / contactEvents.length : 0,
      enemiesNeverSeen: cop.enemiesNeverSeen,
      durationMin: (snap?.tick ?? 0) / TICKS_PER_MIN,
    };
  }, [events, truth, units, grid, snap]);

  // A branch belongs to the run it was flown from: a new payload (a second fight) throws away the
  // counterfactual of the first, so the replay's right pane never shows an alternate history of some
  // other battle.
  useEffect(() => { useBranch.getState().reset(); }, [payload]);

  // Handle for the end-to-end test and for poking at a finished run in the console, the debrief's
  // counterpart to the live stage's `window.__wt`.
  useEffect(() => {
    if (!DEBUG) return;
    const w = window as unknown as Record<string, unknown>;
    w.__wtDebrief = { store: useDebrief, payload, stats, decisionPoints: dps, branch: useBranch };
    return () => { delete w.__wtDebrief; };
  }, [payload, stats, dps]);

  const narrativeCtx = useMemo<DebriefContext>(() => ({
    events,
    orders,
    units,
    lostTime: lostTimeFull.map((lt) => ({ item: lt.item, minutes: lt.minutes })),
    stats: statsRecord(stats),
  }), [events, orders, units, lostTimeFull, stats]);

  if (!payload || !snap || !meta) {
    return (
      <div className="flex h-full items-center justify-center bg-ink">
        <div className="text-center">
          <div className="mono text-[11px] tracking-[0.3em] text-fog">DEBRIEF</div>
          <p className="mt-2 text-[13px] text-fog">No completed run to review yet.</p>
          <button
            type="button"
            onClick={() => setStage('scenario')}
            className="mono mt-4 rounded-sm border border-slate-700 px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] text-paper"
          >
            Back to the scenario
          </button>
        </div>
      </div>
    );
  }

  const outcome = OUTCOME[snap.ended ?? 'unknown'];

  return (
    <div data-testid="debrief-stage" className="thin-scroll h-full overflow-auto bg-ink">
      <header className="sticky top-0 z-10 border-b border-slate-700 bg-slate-900/95 px-4 py-3 backdrop-blur-sm">
        <div className="flex flex-wrap items-center gap-3">
          <Wordmark suffix="debrief" />
          <button
            type="button"
            data-testid="design-record-link"
            onClick={() => setStage('design')}
            className="mono text-[10px] uppercase tracking-[0.15em] text-fog underline decoration-dotted underline-offset-4 hover:text-[var(--color-friendly)]"
            title="The design record: planning-loop workflow map, AI (artificial intelligence) pattern decisions, trade-off log"
          >
            Design record
          </button>
          {/* The audit is the fourth section of the same page; this is a direct way in from the one
              stage where "what did the model actually do in that run?" is the live question. */}
          <button
            type="button"
            data-testid="ai-audit-link"
            onClick={() => {
              setStage('design');
              // The record mounts on the next frame; scroll once it is there.
              requestAnimationFrame(() => {
                document.querySelector('[data-testid="design-section-audit"]')
                  ?.scrollIntoView({ block: 'start' });
              });
            }}
            className="mono text-[10px] uppercase tracking-[0.15em] text-fog underline decoration-dotted underline-offset-4 hover:text-[var(--color-friendly)]"
            title="Every model call this tab has made: purpose, model, prompt size, latency, source, and what validation threw away"
          >
            AI audit
          </button>
          <span
            data-testid="outcome-chip"
            className="mono rounded-sm border px-2 py-1 text-[12px] uppercase tracking-[0.2em]"
            style={{
              borderColor: outcome.tone,
              color: outcome.tone,
              background: `color-mix(in srgb, ${outcome.tone} 12%, transparent)`,
            }}
          >
            {outcome.label}
          </span>
          <span className="mono text-[12px] text-paper">{clockHM(snap.tick)}</span>
          <span className="mono text-[11px] text-fog">
            Yours, of {stats.blueTotal}: {lossesLine(stats.blueLosses, stats.blueBroken)} · enemy, of{' '}
            {stats.redTotal}: {lossesLine(stats.redLosses, stats.redBroken)} ·{' '}
            {Math.round(stats.strengthPct)}% average strength left
          </span>
          <span className="flex-1" />
          {grid && scenario && (
            <ShareChip grid={grid} scenario={scenario} dials={dials} plan={plan} payload={payload} />
          )}
          <button
            type="button"
            data-testid="fight-again"
            onClick={() => setStage('scenario')}
            className="mono rounded-sm border px-3 py-1.5 text-[11px] uppercase tracking-[0.2em]"
            style={{
              borderColor: 'var(--color-friendly)',
              color: 'var(--color-friendly)',
              background: 'color-mix(in srgb, var(--color-friendly) 10%, transparent)',
            }}
          >
            Fight it again
          </button>
        </div>
        <p className="mt-1 text-[11px] text-fog">
          {outcome.note} {scenario ? `${scenario.names.tf} against ${scenario.names.enemy} on ${meta.title}.` : ''}
        </p>
        <p className="mono mt-1 text-[10px] text-fog" data-testid="debrief-ruleset">
          Ruleset: {payload.ruleset.preset}{payload.ruleset.modified ? ' (modified)' : ''}
        </p>
      </header>

      <main className="mx-auto flex max-w-[1400px] flex-col gap-3 p-4">
        <Replay meta={meta} truth={truth} events={events} units={units} graphics={graphics} />

        <DecisionSupport
          dps={dps}
          meta={meta}
          grid={grid}
          scenario={scenario}
          graphics={graphics}
          units={units}
          truth={truth}
          events={events}
          orderLog={payload.orderLog}
        />

        {/* Decision support, continued: the player's own forecasts at those decision points, scored
            against what the enemy actually did, beside the S-2 estimate's answer for the same moment
            (src/ui/debrief/Calibration.tsx). */}
        <Calibration />

        <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
          <OrderLogTable orders={orders} events={events} units={units} graphics={graphics} grid={grid} />
          <DecisionLog events={events} orders={orders} units={units} graphics={graphics} grid={grid} />
        </div>

        <SyncMatrix events={events} units={units} graphics={graphics} />

        <div className="grid grid-cols-1 gap-3 xl:grid-cols-[2fr_1fr]">
          <Stats stats={stats} />
          <LostTime events={events} orders={orders} graphics={graphics} units={units} runTicks={runTicks} />
        </div>

        <Narrative
          ctx={narrativeCtx}
          outcome={outcome.label}
          durationMin={stats.durationMin}
          decisions={decisions}
          lostTimeFull={lostTimeFull}
          events={events}
          units={units}
          grid={grid}
          decisionPointsSummary={dpSummary}
        />
      </main>
    </div>
  );
}
